import React, { useEffect, useState } from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import styled from 'styled-components';
import Stages from './Stages';
import {
  allIndicatorsValid,
  getPreviousAndNextStage,
  IndicatorByService,
  IndicatorHeaderList,
  IndicatorSectionKey,
  IndicatorStagesProps,
  IndicatorWithUpdate,
  isIndicatorListValid,
  validateAllIndicators,
} from './utils';
import { scrollToTop } from 'shared/utils/pageUtils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import ImpactHeaders from './ImpactHeaders';
import { ImpactSummarySupportText } from './SupportText';
import { ReportPeriod } from '../SectionPeriod/utils';
import { GuidanceTextResponse } from '../utils';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import LockImpactSummary from './LockImpactSummary';

const isValid = (
  selectedIndicators: IndicatorByService,
  selectedSectionKey: IndicatorSectionKey,
  isFinalStep: boolean,
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
): boolean => {
  if (isFinalStep) {
    return validateAllIndicators(
      selectedIndicators,
      capacityDevelopmentEnabled,
      learnEnabled,
      actEnabled,
    );
  }
  const validations = isIndicatorListValid(
    selectedIndicators[selectedSectionKey],
    capacityDevelopmentEnabled,
    learnEnabled,
    actEnabled,
    selectedSectionKey,
  );
  return validations.isValid && validations.isAnySeen;
};

const getFirstIncompleteSection = (
  selectedIndicators: IndicatorByService,
  indicatorHeaderList: IndicatorHeaderList[],
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
  sectionKey: IndicatorSectionKey,
): IndicatorHeaderList => {
  for (const key of indicatorHeaderList) {
    const indicators = selectedIndicators[key.sectionKey];
    const validations = allIndicatorsValid(
      indicators,
      capacityDevelopmentEnabled,
      learnEnabled,
      actEnabled,
      sectionKey,
    );
    if (!validations.isValid) return key;
  }
  return indicatorHeaderList[indicatorHeaderList.length - 1];
};

export interface IndicatorStageRenderProps {
  isDebug: boolean;
  isImpactSummaryCompleted?: boolean;
  guidanceText: GuidanceTextResponse | undefined;
  indicatorHeaderList: IndicatorHeaderList[];
  maxWidth: number;
  beneficiaryTerm?: string;
  programName?: string;
  intermediaries?: string;
  serviceGoal?: string;
  serviceType?: string;
  isReportPassed: boolean;
  reportPeriod: ReportPeriod;
  title: IndicatorStagesProps['title'];
  capacityDevelopmentEnabled: IndicatorStagesProps['capacityDevelopmentEnabled'];
  learnEnabled: IndicatorStagesProps['learnEnabled'];
  actEnabled: IndicatorStagesProps['actEnabled'];
  service: IndicatorStagesProps['service'];
  services: IndicatorStagesProps['services'];
  reportId: IndicatorStagesProps['reportId'];
  questionId: IndicatorStagesProps['questionId'];
  choiceIsSelected: IndicatorStagesProps['choiceIsSelected'];
  pageSectionProps: IndicatorStagesProps['pageSectionProps'];
  handleNext: IndicatorStagesProps['handleNext'];
  handleBack: IndicatorStagesProps['handleBack'];
  handleSaveAndContinue: () => unknown;
  firstSectionKey: IndicatorSectionKey;
  defaultIndicators: IndicatorByService;
  unselectedIndicators: IndicatorByService;
  selectedIndicators: IndicatorByService;
  deletedIndicators: IndicatorByService;
  handleDetailOpen: () => unknown;
  handleDetailClose: () => unknown;
  handleRestoreIndicator: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    index: number,
  ) => unknown;
  handleIndicatorTextUpdate: (
    key: IndicatorSectionKey,
    text: string,
    index: number,
  ) => unknown;
  handleIndicatorSeenUpdate: (
    key: IndicatorSectionKey,
    seen: boolean,
    index: number,
  ) => unknown;
  handleAddIndicator: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    index: number,
  ) => unknown;
  handleDeleteIndicator: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    index: number,
  ) => unknown;
}

const StyledDiv = styled.div<{ maxWidth: number }>`
  text-align: center;
  padding-top: ${SPACERS.large};
  padding-bottom: 160px;
  .question-title {
    margin-bottom: ${SPACERS.large};
  }
  .heading,
  .subheading {
    max-width: 750px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.medium};
  }
  .bottom-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 28px;
    margin-top: ${SPACERS.medium};
    button {
      margin-left: ${SPACERS.tiny};
      margin-right: ${SPACERS.tiny};
    }
  }
  .open-details {
    a {
      cursor: pointer;
      color: ${COLORS.ctaPrimary};
    }
  }
  .indicator-cards-container {
    position: relative;
    max-width: ${({ maxWidth }) => maxWidth}px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const IndicatorStageRender: React.FC<IndicatorStageRenderProps> = ({
  isDebug,
  indicatorHeaderList,
  maxWidth,
  title,
  capacityDevelopmentEnabled,
  learnEnabled,
  actEnabled,
  serviceGoal,
  serviceType,
  firstSectionKey,
  beneficiaryTerm,
  intermediaries,
  reportPeriod,
  programName,
  isReportPassed,
  service,
  services,
  reportId,
  questionId,
  deletedIndicators,
  selectedIndicators,
  unselectedIndicators,
  handleRestoreIndicator,
  handleBack,
  handleNext,
  handleDetailOpen,
  handleAddIndicator,
  handleDeleteIndicator,
  handleIndicatorSeenUpdate,
  handleIndicatorTextUpdate,
  isImpactSummaryCompleted,
}) => {
  const { answersBySections } = useIndicator();
  const [indicatorHeader, setIndicatorHeader] = useState<IndicatorHeaderList>(
    indicatorHeaderList[0],
  );

  useEffect(() => {
    scrollToTop();
  }, [indicatorHeader]);

  useEffect(() => {
    if (isImpactSummaryCompleted) {
      setIndicatorHeader(
        getFirstIncompleteSection(
          selectedIndicators,
          indicatorHeaderList,
          capacityDevelopmentEnabled,
          learnEnabled,
          actEnabled,
          indicatorHeaderList[0].sectionKey,
        ),
      );
    } else {
      setIndicatorHeader(indicatorHeaderList[0]);
    }
  }, []);

  const handleStage = (stage: IndicatorHeaderList) => () => {
    setIndicatorHeader(stage);
  };

  const { previous, next } = getPreviousAndNextStage(
    indicatorHeaderList,
    indicatorHeader,
    capacityDevelopmentEnabled,
    learnEnabled,
    actEnabled,
  );

  if (
    !isDebug &&
    answersBySections.indicator_details &&
    answersBySections.indicator_details.length > 0
  ) {
    return <LockImpactSummary handleNext={handleNext} />;
  }

  return (
    <StyledDiv maxWidth={maxWidth}>
      <TrueImpactText variant="heading-label" color="blackBase">
        Definition progress
      </TrueImpactText>
      <ImpactHeaders
        list={indicatorHeaderList}
        onClick={setIndicatorHeader}
        selectedText={indicatorHeader}
        selectedIndicators={selectedIndicators}
        capacityDevelopmentEnabled={capacityDevelopmentEnabled}
        learnEnabled={learnEnabled}
        actEnabled={actEnabled}
      />
      <ImpactSummarySupportText
        isReportPassed={isReportPassed}
        beneficiaryTerm={beneficiaryTerm}
        programName={programName}
        sectionKey={indicatorHeader.sectionKey}
        className="question-title"
      />
      <div className="indicator-cards-container">
        <Stages
          service={service}
          services={services}
          section={indicatorHeader}
          questionId={questionId}
          reportId={reportId}
          maxWidth={maxWidth}
          reportPeriod={reportPeriod}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName}
          serviceGoal={serviceGoal}
          serviceType={serviceType}
          intermediaries={intermediaries}
          isReportPassed={isReportPassed}
          handleAddIndicator={handleAddIndicator}
          handleDeleteIndicator={handleDeleteIndicator}
          handleTextUpdate={handleIndicatorTextUpdate}
          handleRestoreIndicator={handleRestoreIndicator}
          handleIndicatorSeenUpdate={handleIndicatorSeenUpdate}
          indicators={unselectedIndicators[indicatorHeader.sectionKey]}
          deletedIndicators={deletedIndicators[indicatorHeader.sectionKey]}
          selectedIndicators={selectedIndicators[indicatorHeader.sectionKey]}
        />
      </div>
      <div className="bottom-buttons">
        <TrueImpactButton
          onClick={previous ? () => setIndicatorHeader(previous) : handleBack}
          variant="secondary-large"
        >
          {previous ? 'Previous Stage' : 'Go Back'}
        </TrueImpactButton>
        <TrueImpactButton
          disabled={
            !isValid(
              selectedIndicators,
              indicatorHeader.sectionKey,
              !next,
              capacityDevelopmentEnabled,
              learnEnabled,
              actEnabled,
            )
          }
          onClick={next ? handleStage(next) : handleDetailOpen}
        >
          {next ? 'Next Stage' : 'Go to Review'}
        </TrueImpactButton>
      </div>
      <TrueImpactText
        variant="body-small"
        className="open-details"
        onClick={handleDetailOpen}
      >
        Done this before? <a>Go to quick-add view.</a>
      </TrueImpactText>
    </StyledDiv>
  );
};

export default IndicatorStageRender;
